import { getHttpStatus } from '../../../../utils/get-http-status';

/**
 * Um `POST /user` por e-mail na sessão do checkout.
 *
 * O membro é criado no blur de nome/e-mail/telefone E de novo no submit (que
 * chama `handleNameAndEmailBlur`). Antes, cada passagem apagava o
 * `access_token` e repetia o `POST /user`: a segunda levava 409 (o membro
 * acabou de ser criado pelo blur), o token não voltava e o comprador novo
 * chegava à plataforma com `?access_token=` vazio — sem senha, sem conseguir
 * entrar.
 *
 * Regra: mesmo e-mail → não cria de novo e o token lembrado continua valendo;
 * e-mail diferente → o token do e-mail anterior não serve, cria de novo.
 */
export type RememberedMember = {
	/** Normalizado com `normalizeEmail`. */
	email: string;
	/** `null` quando o membro já existia (409 ou cadastro sem token). */
	accessToken: string | null;
};

export const normalizeEmail = (email: string): string =>
	email.trim().toLowerCase();

/** O e-mail digitado é o do membro já tratado nesta sessão? */
export const isSameMember = (
	remembered: RememberedMember | null,
	email: string
): boolean => !!remembered && remembered.email === normalizeEmail(email);

/** 409 do `POST /user`: o e-mail já tem membro na plataforma. */
export const isMemberAlreadyExistsError = (error: unknown): boolean =>
	getHttpStatus(error) === 409;

/** Um `POST /user` emitido: a ordem (crescente) e o e-mail que ele carregou. */
export type MemberRequest = { seq: number; email: string };

/**
 * A resposta de um `POST /user` só pode ser aplicada se o pedido ainda é o
 * MAIS RECENTE e o e-mail dele é o que está na tela agora.
 *
 * Os blurs não são esperados e o submit pode cruzar com um blur: o pedido do
 * e-mail A pode resolver DEPOIS de o comprador ter trocado para B. Sem esta
 * regra, o token de A era gravado por cima da limpeza feita para B — e B
 * levava a sessão de A no redirect.
 */
export const isCurrentMemberRequest = (
	issued: MemberRequest,
	latestSeq: number,
	currentEmail: string
): boolean =>
	issued.seq === latestSeq && issued.email === normalizeEmail(currentEmail);
